import { useEffect, useMemo, useRef, useState } from 'react';
import type { ClaimResponse, RosterEntry } from '@mv/contracts';
import { IconForward } from './Icon';
import { claimName, loadRoster, requestClaimHelp } from '../lib/claim';
import { search } from '../lib/roster';

/**
 * "Which of these is you?" — the first half of the welcome screen (D59).
 *
 * **The invariant this component is written around: there is always exactly one
 * enabled thing to tap.** Every branch below ends in either a live picker, a
 * retry, or the escalation path. A guest standing at check-in who cannot get
 * past this screen cannot use the app at their friend's wedding, so a dead end
 * here is the worst outcome in the whole feature — worse than an impostor,
 * which is recoverable.
 *
 * Nothing is written until the confirm step. Tapping a name opens a dialog that
 * reads it back in full; the claim happens on the second tap. That is the
 * difference between a mis-tap with no consequence and one that needs an
 * organiser to undo.
 */
type Phase =
  | { k: 'loading' }
  | { k: 'picking' }
  | { k: 'confirming'; entry: RosterEntry }
  | { k: 'claiming'; entry: RosterEntry }
  | { k: 'help' };

const MESSAGES: Record<string, string> = {
  taken: 'Somebody has already been matched to that name. If that name is yours, tell us below and an organiser will sort it out.',
  'held-elsewhere': 'You are already matched to a different name. Ask an organiser to release it first.',
  'unknown-guest': 'That name is no longer on the list. Search again.',
  network: 'We could not reach the server. Check your connection and try again.',
};

export default function RosterPicker(
  { onClaimed, onSwitchAccount, email, userId }: {
    onClaimed: (r: ClaimResponse, refreshed: boolean) => void;
    /** Sign out and return to the start. Routing lives in `Welcome.tsx`, so
     *  the picker asks for this rather than navigating itself. */
    onSwitchAccount: () => void;
    /**
     * The caller's own address. Rendered in two places: the `taken` hint, and
     * the escalation fallback. It has to be passed in.
     *
     * `Welcome.tsx` returns early into this component, so its "Signed in as…"
     * line never renders while the picker is up — and Google is the primary
     * sign-in path (D10), where nothing between the one tap and this screen
     * ever shows a guest their own address. Telling them to read it off "the
     * previous screen" pointed at a screen that does not exist for most guests.
     *
     * **Always the caller's, never the holder's.** Both render sites depend on
     * that: the hint would otherwise leak the real guest's address to whoever
     * is at the keyboard.
     */
    email: string;
    userId: string;
  },
) {
  const [phase, setPhase] = useState<Phase>({ k: 'loading' });
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<{ reason: string; message: string } | null>(null);
  const [note, setNote] = useState('');
  const [helpState, setHelpState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const searchBox = useRef<HTMLInputElement>(null);

  useEffect(() => { void fetchRoster(); }, []);

  /**
   * `keepError` exists because of a real bug rather than a preference.
   *
   * `confirm` sets the "that name is gone" message and then calls this, and
   * React batches every state write in that tick — so `setError(null)` here
   * landed last and the message never rendered at all. The guest saw a loading
   * flash and an empty search box, with no reason their tap did nothing.
   */
  async function fetchRoster({ keepError = false } = {}) {
    setPhase({ k: 'loading' });
    if (!keepError) setError(null);
    try {
      setRoster(await loadRoster());
      setPhase({ k: 'picking' });
    } catch {
      // F8, with no cache to fall back on. The picker cannot be shown, so the
      // two things that remain are a retry and the escalation path — and both
      // are rendered, because neither alone is a way out of this state.
      setRoster([]);
      setError({ reason: 'load', message: 'We could not load the guest list.' });
      setPhase({ k: 'picking' });
    }
  }

  const results = useMemo(() => search(roster, query), [roster, query]);
  /** Open once there is a query worth searching on — the same two-character
   *  floor `search` itself applies, so the card never opens on nothing. */
  const open = query.trim().length >= 2;

  async function confirm(entry: RosterEntry) {
    setPhase({ k: 'claiming', entry });
    setError(null);
    const out = await claimName(entry.guestId);
    if (out.ok) { onClaimed(out.result, out.refreshed); return; }
    // Back to a live picker with the reason on screen, never a dead end (F4/F5).
    setError({ reason: out.reason, message: MESSAGES[out.reason] ?? MESSAGES.network });
    setPhase({ k: 'picking' });
    if (out.reason === 'unknown-guest') void fetchRoster({ keepError: true });
    searchBox.current?.focus();
  }

  async function sendHelp() {
    setHelpState('sending');
    setHelpState(await requestClaimHelp(note) ? 'sent' : 'failed');
  }

  if (phase.k === 'loading') {
    return <p role="status" style={{ opacity: 0.75 }}>Loading the guest list…</p>;
  }

  if (phase.k === 'help') {
    return (
      <div className="auth-col">
        <h2 style={{ margin: '0 0 4px' }}>We'll find you</h2>
        {helpState === 'sent' ? (
          <p role="status">
            Thanks — an organiser will match you up. You can close this and come back later.
          </p>
        ) : (
          <>
            <p style={{ fontSize: 13, opacity: 0.75 }}>
              Tell us who you are and we'll sort it out. If you're a plus-one, say whose.
            </p>
            <div className="field">
              <label htmlFor="claim-note">Your name, and anything that helps</label>
              <textarea
                id="claim-note" className="input" rows={3} value={note} maxLength={280}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            {/* F11 — the backstop for four other failure modes has its own,
                and it needs no network: an organiser can assign a row from
                these two values alone. So they are rendered here rather than
                referred to. */}
            {helpState === 'failed' && (
              <div role="alert" className="form-error">
                <p style={{ margin: '0 0 8px' }}>
                  We couldn't send that. Show an organiser this screen — they can match you
                  up from these.
                </p>
                <dl className="claim-fallback">
                  <dt>Signed in as</dt><dd>{email}</dd>
                  <dt>Account</dt><dd><code>{userId}</code></dd>
                </dl>
              </div>
            )}
            <button
              type="button" className="btn btn-primary btn-block"
              onClick={() => { void sendHelp(); }}
              disabled={helpState === 'sending'}
            >
              {helpState === 'sending' ? 'Sending…' : helpState === 'failed' ? 'Try again' : 'Send'}
            </button>
          </>
        )}
        <button type="button" className="btn btn-block" onClick={() => setPhase({ k: 'picking' })}>
          Back to the list
        </button>
      </div>
    );
  }

  const busy = phase.k === 'claiming';

  return (
    <div className="auth-col">
      <h2 style={{ margin: '0 0 4px' }}>Which one is you?</h2>
      <p style={{ fontSize: 13, opacity: 0.75, margin: '0 0 18px' }}>
        Find your name on the guest list. This is a one-off — we'll remember next time.
      </p>

      {error && (
        <div role="alert" className="form-error">
          <p style={{ margin: 0 }}>{error.message}</p>
          {/* The wrong-email case, which `taken` cannot distinguish from an
              impostor and should not try to: both look like "that row is
              held by a userId that is not yours". So this names the one fact
              the guest can act on — the address *they* are signed in with —
              and leaves the escalation path below for when it really is
              somebody else.

              It shows their own address rather than a masked form of the
              holder's. A mask (`abc****123@gmail.com`) leaks the real
              guest's address to whoever is at the keyboard, and buys nothing
              in exchange: 9 of the 10 accounts on this stack are gmail, so
              the visible half of the mask is the half they already guessed. */}
          {error.reason === 'taken' && (
            <p style={{ margin: '8px 0 0' }}>
              If you've used a different email here before, that's the likely cause.
              You're signed in as <strong>{email}</strong>.{' '}
              <button type="button" className="btn-link" onClick={onSwitchAccount}>
                Sign in with another account
              </button>
            </p>
          )}
        </div>
      )}

      {/* `position: relative`, so the dropdown hangs off the field and overlays
          what is below rather than pushing it down the screen. */}
      <div className="field roster-field">
        <label htmlFor="roster-q">Start typing your name</label>
        <input
          id="roster-q" ref={searchBox} className="input" autoComplete="off"
          style={{ minHeight: 50, fontSize: 16 }}
          value={query} onChange={(e) => setQuery(e.target.value)} disabled={busy}
          role="combobox" aria-expanded={open} aria-controls="roster-list" aria-autocomplete="list"
        />

        {open && (
          <ul className="roster-dropdown" id="roster-list" role="listbox" aria-label="Matching names">
            {results.map((e) => (
              <li key={e.guestId} role="presentation">
                <button
                  type="button" role="option" aria-selected="false"
                  className="roster-row" disabled={busy}
                  onClick={() => setPhase({ k: 'confirming', entry: e })}
                >
                  <span>{e.fullName}</span>
                  <IconForward size={16} aria-hidden="true" />
                </button>
              </li>
            ))}
            {results.length === 0 && (
              <li className="roster-empty" role="presentation">No match for “{query}”.</li>
            )}
          </ul>
        )}
      </div>

      {/* Always rendered, at every phase of the picker — C7. It is the way out
          of an empty result, an empty roster, and a name somebody else holds. */}
      <button
        type="button" className="btn btn-block" style={{ marginTop: 12 }}
        onClick={() => setPhase({ k: 'help' })}
      >
        I can't find myself
      </button>

      {error && !roster.length && (
        <button type="button" className="btn btn-block" style={{ marginTop: 8 }} onClick={() => { void fetchRoster(); }}>
          Try loading the list again
        </button>
      )}

      {phase.k === 'confirming' && (
        <div className="sheet-scrim" role="dialog" aria-modal="true" aria-label="Confirm your name">
          <div className="sheet">
            {/* The name in full, read back. There are no duplicate names on the
                list, so what this protects against is the mis-tap — the row
                above or below the one intended. */}
            <p style={{ margin: '0 0 4px', fontSize: 13, opacity: 0.75 }}>You are</p>
            <h4 className="claim-name">{phase.entry.fullName}</h4>
            <p style={{ fontSize: 13, opacity: 0.75, margin: '0 0 14px' }}>
              We'll link this name to your sign-in, so only you can use it.
            </p>
            <button
              type="button" className="btn btn-primary btn-block"
              onClick={() => { void confirm(phase.entry); }}
            >
              Yes, that's me
            </button>
            <button
              type="button" className="btn btn-block" style={{ marginTop: 8 }}
              onClick={() => setPhase({ k: 'picking' })}
            >
              No, go back
            </button>
          </div>
        </div>
      )}

      {busy && <p role="status" style={{ marginTop: 8, opacity: 0.75 }}>Matching you up…</p>}
    </div>
  );
}
